// @flow
import React from 'react';
import cx from 'classnames';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import { Picker, Uploader, UploadTextAndButton, CSVUploadLoadingState } from 'components/File';
import { PRIVATE_UPLOAD } from './TargetByStudentIdUploadPageContainer';
import DropZoneModal from 'components/DropZoneModal';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import TargetByStudentIdUploadPageSidebar from './TargetByStudentIdUploadPageSidebar';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

const prefixEvent = (suffix: string) => `announcements.target_by_student_id.upload_list.${suffix}`;
function trackEvent(key: string, properties?: Object = {}) {
  EventTracking.trackKey(prefixEvent(key), properties);
}
function trackUploadErrorView() {
  trackEvent('upload_error.view');
}
function trackUploadClick() {
  trackEvent('upload_file.click');
}

type Props = {
  schools: ?(ComposerOrganization[]),
  selectedSchoolUuid: string,
  onOrgSelection: (schoolUuid: string) => void,
  csvIsMatching: boolean,
  csvMatchError?: ?Error,

  // from container
  onUploadSuccess: (file: *) => void,
  uploadFile: (file: *, onUploadProgress: Function, onUploadSuccess: Function) => void,

  // Drag 'n drop props
  connectDropTarget: Function,
  isDraggingFile?: boolean,
};

type State = {
  selectedSchool: ?ComposerOrganization,
  csvIsLoading: boolean,
};

class TargetByStudentIdUploadPageBody extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      csvIsLoading: false,
      selectedSchool: null,
    };
  }

  componentDidMount() {
    trackEvent('view');
    if (this.refs.select) {
      this.refs.select.focus();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isDraggingFile && this.props.isDraggingFile) {
      trackEvent('upload_file.drag');
    }
  }

  selectSchool = (schoolUuid: string) => {
    const { schools } = this.props;
    const selectedSchool = schools && schools.find(s => s.uuid === schoolUuid);

    if (!selectedSchool) {
      ToastActions.showErrorToast(
        'Oops! Looks like something went wrong. Please select another school.'
      );
      console.error('TBSI - tried to select a nonexistent school');
      return;
    }

    trackEvent('org_dropdown_item.click', { orgUuid: schoolUuid });
    this.props.onOrgSelection(schoolUuid);
    this.setState({ selectedSchool });
  };

  renderDropzoneModal = (): ?React$Element<*> => {
    const { isDraggingFile } = this.props;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal titleDrop="Drop a CSV file here" isHovering />
      </div>
    );
  };

  renderMain = () => {
    const { schools, selectedSchoolUuid, csvIsMatching, csvMatchError } = this.props;
    const { csvIsLoading, selectedSchool } = this.state;
    const schoolOptions = schools
      ? schools
          .filter(school => school.permissions.canSendToStudentIdList)
          .map(school => {
            return { label: school.name, value: school.uuid };
          })
      : [];
    const loadingText = csvIsMatching
      ? 'Matching student numbers to students on Remind...'
      : 'Uploading your CSV file...';
    const selectedSchoolName = selectedSchool && selectedSchool.name;
    const percentOfStudentsAtSelectedSchoolWithIds: ?number =
      selectedSchool && Math.floor(selectedSchool.studentIdPercentage * 100);

    return (
      <div className="TargetByStudentIdUploadPage-main">
        <Row size="small">
          <Label>School</Label>
          <Select
            value={this.props.selectedSchoolUuid}
            options={schoolOptions}
            placeholder="Choose a school"
            onChange={schoolUuid => this.selectSchool(schoolUuid)}
            onMenuOpen={() => trackEvent('org_dropdown.view')}
            ref="select"
          />
          {selectedSchoolName &&
            typeof percentOfStudentsAtSelectedSchoolWithIds === 'number' &&
            percentOfStudentsAtSelectedSchoolWithIds < 50 && (
              <MediaObject className="TargetByStudentIdUploadPage-fieldError" gutter="xsmall">
                <Glyph name="caution" size="xsmall" color="pencil" />
                <Text color="chalkboard">
                  {percentOfStudentsAtSelectedSchoolWithIds}% of students at {selectedSchoolName}{' '}
                  have student numbers on Remind. You’ll only be able to message students with
                  student numbers.{' '}
                  <Link
                    href={LinkConstants.UNMATCHED_STUDENTS}
                    target="_blank"
                    eventKey={prefixEvent('org_dropdown_cta.click')}>
                    Learn more here
                  </Link>
                </Text>
              </MediaObject>
            )}
        </Row>
        <div
          className={cx('TargetByStudentIdUploadPage-dropzone', {
            'TargetByStudentIdUploadPage-dropzone--disabled': !selectedSchoolUuid,
          })}>
          {this.renderDropzoneModal()}
          {csvIsMatching || csvIsLoading ? (
            <CSVUploadLoadingState text={loadingText} />
          ) : (
            <Picker acceptedFileTypes=".csv">
              {({ pick, file }) => {
                if (file) {
                  return (
                    <Uploader
                      file={file}
                      onUploadSuccess={this.props.onUploadSuccess}
                      private={PRIVATE_UPLOAD}>
                      {() => <CSVUploadLoadingState text={loadingText} />}
                    </Uploader>
                  );
                }
                if (csvMatchError) {
                  return (
                    <UploadTextAndButton
                      onClick={trackUploadClick}
                      onErrorView={trackUploadErrorView}
                      size="small"
                      pick={pick}
                      error={csvMatchError.message}
                    />
                  );
                }
                return (
                  <UploadTextAndButton
                    onClick={trackUploadClick}
                    onErrorView={trackUploadErrorView}
                    size="large"
                    pick={pick}
                  />
                );
              }}
            </Picker>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { connectDropTarget } = this.props;

    return (
      <React.Fragment>
        <TargetByStudentIdUploadPageSidebar />
        {this.props.selectedSchoolUuid ? connectDropTarget(this.renderMain()) : this.renderMain()}
      </React.Fragment>
    );
  }
}

const dndEvents = {
  drop: (props, monitor, component) => {
    const { files } = monitor.getItem();
    if (files.length > 1) {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to upload more than one file - please try again'
      );
      return;
    }
    const file = files[0];
    const onUploadProgress = () => {
      component.setState({
        csvIsLoading: true,
      });
    };
    const onUploadSuccess = data => {
      if (props.onUploadSuccess) {
        props.onUploadSuccess(data);
      }
      component.setState({
        csvIsLoading: false,
      });
    };
    props.uploadFile(file, onUploadProgress, onUploadSuccess);
  },
  canDrop: props => {
    return !props.csvIsMatching; // can only attach one file at a time
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

export { TargetByStudentIdUploadPageBody };

export default withDropTarget(TargetByStudentIdUploadPageBody);
